import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    CircularProgress,
    Snackbar,
    LinearProgress,
    Alert,
    Divider,
    Badge,
    FormControlLabel,
    Checkbox,
    Tooltip,
} from '@mui/material';
import ReactFeather from 'feather-icons-react';
import jwt_decode from 'jwt-decode';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { SignatureCreator } from '../../../../lease/components/steps/components';
import { getSignedUrl, MultiDocumentUploader } from '../../../utils';
import Swal from 'sweetalert2';
import { Previewer } from '../../../../../../../../../../components/DocumentPreviewer/Previewer';

export const extractKeyFromUrl = (url, bucketName = 'rentdigicare') => {
    const urlObj = new URL(url);
    let pathname = urlObj.pathname.slice(1);
    if (pathname.startsWith(`${bucketName}/`)) {
        pathname = pathname.slice(bucketName.length + 1);
    }
    return pathname;
};

const Step7 = ({ formik, token, isPreviewMode = false, onCompletionStatusChange }) => {
    const { values, setFieldValue } = formik;
    const { property_id, attached_documents = [], tenants = [] } = values;
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewDocument, setPreviewDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSignatureCreatorOpen, setIsSignatureCreatorOpen] = useState(false);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(null);
    const [progress, setProgress] = useState(0);
    const [showProgress, setShowProgress] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [documentToUpload, setDocumentToUpload] = useState(null);
    const [maxFilesAllowed, setMaxFilesAllowed] = useState(5);
    const [showInlineSignature, setShowInlineSignature] = useState(false);
    const [reviewedDocuments, setReviewedDocuments] = useState(new Set());

    let currentTenant = null;
    if (!isPreviewMode && token) {
        try {
            const pathParts = window.location.pathname.split('/');
            const urlToken = pathParts[pathParts.length - 1];
            const { tenantId } = jwt_decode(urlToken);
            currentTenant = tenants.find(tenant => tenant.id === tenantId);
        } catch (error) {
            console.error("Error decoding token:", error);
        }
    }

    const isValidValue = (value) => {
        return value !== null && value !== undefined && value !== '' && value.toString().trim() !== '';
    };

    const safeArrayLength = (arr) => {
        return Array.isArray(arr) ? arr.length : 0;
    };

    const safeFindInArray = (arr, predicate) => {
        return Array.isArray(arr) ? arr.find(predicate) : undefined;
    };

    const getDocumentUrls = (document) => {
        if (!document.urls || document.urls.length === 0) {
            return ["upload_requested"];
        }
        return document.urls.map(url => url.file_url).filter(url => url && url !== "upload_requested");
    };

    const isUploadRequired = (document) => {
        const urls = getDocumentUrls(document);
        return urls.length === 0 || urls.every(url => url === "upload_requested");
    };

    const getValidUrlsCount = (document) => {
        const urls = getDocumentUrls(document);
        return urls.filter(url => url !== "upload_requested").length;
    };

    const isSignatureRequiredForTenant = (document, tenantId) => {
        if (isPreviewMode) {
            return document.signature_required_from?.some(req => req.id === tenantId);
        }
        return document.signature_required_from?.some(req => req.id === tenantId);
    };

    const hasTenantSigned = (document, tenantId) => {
        return document.signatures?.some(sig =>
            sig.id === tenantId && sig.signature && sig.signature.trim() !== ''
        );
    };

    const areAllDocumentsCompleted = () => {
        if (!currentTenant) return false;

        return attached_documents.every(document => {
            const isUploadReq = isUploadRequired(document);
            const isSignatureReq = isSignatureRequiredForTenant(document, currentTenant.id);
            const isSigned = hasTenantSigned(document, currentTenant.id);
            const isReviewed = reviewedDocuments.has(document.id);

            if (isUploadReq) return false;
            if (isSignatureReq && !isSigned) return false;
            if (!isUploadReq && !isSignatureReq && !isReviewed) return false;

            return true;
        });
    };

    useEffect(() => {
        const isCompleted = areAllDocumentsCompleted();
        onCompletionStatusChange?.(isCompleted);
    }, [attached_documents, reviewedDocuments, currentTenant, onCompletionStatusChange]);

    const fetchSignedUrls = async (document) => {
        setIsLoading(true);
        setError(null);
        try {
            const urls = getDocumentUrls(document);
            if (urls.length === 0 || urls.every(url => url === "upload_requested")) {
                throw new Error("No valid documents uploaded yet");
            }

            const signedUrlPromises = urls.map(async (url) => {
                try {
                    const key = extractKeyFromUrl(url);
                    const { signedUrl } = await getSignedUrl({ key }, token);
                    return { file_url: url, signedUrl, error: null };
                } catch (e) {
                    console.error(`Failed to fetch signed URL for ${url}:`, e);
                    return { file_url: url, signedUrl: null, error: `Failed to fetch ${url}` };
                }
            });

            const signedUrlsResults = await Promise.all(signedUrlPromises);
            return signedUrlsResults;
        } catch (err) {
            setError(err.message || "Failed to fetch document previews.");
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const handlePreviewClick = async (document, documentIndex = null) => {
        const urls = getDocumentUrls(document);
        if (urls.length > 0 && !urls.every(url => url === "upload_requested")) {
            setPreviewDocument({
                ...document,
                loadingUrls: urls.map(() => true),
                signedUrlsResults: [],
                documentIndex,
            });
            setPreviewOpen(true);

            const signedUrlsResults = await fetchSignedUrls(document);
            setPreviewDocument(prev => ({
                ...prev,
                signedUrlsResults,
                loadingUrls: urls.map(() => false),
            }));
        } else {
            setError("Document is not available for preview");
        }
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setShowInlineSignature(false);
        setError(null);
        setTimeout(() => setPreviewDocument(null), 300);
    };

    const handleAction = async (action, document, index) => {
        if (!document) {
            console.error("Invalid document for action:", action);
            return;
        }

        switch (action) {
            case "upload":
                if (isPreviewMode) {
                    setError("Upload not available in preview mode");
                    return;
                }
                try {
                    setDocumentToUpload(document);
                    setMaxFilesAllowed(5);
                    setIsUploadModalOpen(true);
                } catch (err) {
                    setMaxFilesAllowed(5);
                    setDocumentToUpload(document);
                    setIsUploadModalOpen(true);
                }
                break;
            case "sign":
                if (isPreviewMode) {
                    setError("Signing not available in preview mode");
                    return;
                }
                setCurrentDocumentIndex(index);
                setIsSignatureCreatorOpen(true);
                break;
            case "preview_and_sign":
                handlePreviewClick(document, index);
                break;
            case "review":
                handlePreviewClick(document, index);
                break;
            default:
                console.warn("Unknown action:", action);
                break;
        }
    };

    const handleDownloadDocument = async (doc) => {
        const urls = getDocumentUrls(doc);
        const validUrls = urls.filter(url => url !== "upload_requested");

        if (validUrls.length === 0) {
            setError("No documents available for download");
            return;
        }

        try {
            setShowProgress(true);
            setProgress(0);

            if (validUrls.length === 1) {
                await downloadSingleFile(doc, validUrls[0]);
            } else {
                await downloadMultipleFiles(doc, validUrls);
            }

            setProgress(100);
            setTimeout(() => {
                setShowProgress(false);
                setProgress(0);
            }, 1500);
        } catch (err) {
            console.error(err);
            setError("Failed to download document(s). Please try again.");
            setShowProgress(false);
        }
    };

    const downloadSingleFile = async (doc, url) => {
        const signedUrlsResults = await fetchSignedUrls(doc);
        const signedUrl = signedUrlsResults.find(result => result.file_url === url)?.signedUrl;

        if (!signedUrl) throw new Error("Failed to fetch signed URL");

        const response = await fetch(signedUrl);
        if (!response.ok) throw new Error("Failed to fetch document");

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = doc.name || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    };

    const downloadMultipleFiles = async (doc, urls) => {
        const signedUrlsResults = await fetchSignedUrls(doc);

        for (let i = 0; i < urls.length; i++) {
            const url = urls[i];
            const signedUrl = signedUrlsResults.find(result => result.file_url === url)?.signedUrl;

            if (signedUrl) {
                const response = await fetch(signedUrl);
                if (response.ok) {
                    const blob = await response.blob();
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = downloadUrl;
                    link.download = `${doc.name}_${i + 1}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(downloadUrl);

                    setProgress(((i + 1) / urls.length) * 100);

                    if (i < urls.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                }
            }
        }
    };

    const handleDocumentUploaded = (updatedDocument) => {
        if (isPreviewMode) return;

        if (!currentTenant) {
            Swal.fire({
                title: 'Error',
                text: 'Unable to identify current tenant.',
                icon: 'error',
                timer: 3000,
            });
            return;
        }

        const updatedDocuments = attached_documents.map(doc => {
            if (doc.id === updatedDocument.id) {
                return {
                    ...doc,
                    urls: updatedDocument.urls
                        .filter(url => url.file_url !== "upload_requested")
                        .map(url => ({
                            file_url: url.file_url,
                            uploaded_by: currentTenant.id,
                            uploaded_by_name: currentTenant.name,
                            uploaded_at: new Date().toISOString(),
                        })),
                };
            }
            return doc;
        });

        setFieldValue('attached_documents', updatedDocuments);

        Swal.fire({
            title: 'Success!',
            text: `Document "${updatedDocument.name}" has been uploaded successfully.`,
            icon: 'success',
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const handleSignatureCreated = ({ signatureData }) => {
        if (isPreviewMode) return;

        const documentIndex = currentDocumentIndex !== null ? currentDocumentIndex : previewDocument?.documentIndex;

        if (signatureData && documentIndex !== null && currentTenant) {
            const today = new Date().toISOString().split('T')[0];

            const updatedDocuments = [...attached_documents];
            const currentDocument = updatedDocuments[documentIndex];

            if (!currentDocument.signatures) {
                currentDocument.signatures = [];
            }

            const existingSignatureIndex = currentDocument.signatures.findIndex(
                sig => sig.id === currentTenant.id
            );

            const newSignature = {
                id: currentTenant.id,
                name: currentTenant.name,
                signature: signatureData,
                date: today
            };

            if (existingSignatureIndex >= 0) {
                currentDocument.signatures[existingSignatureIndex] = newSignature;
            } else {
                currentDocument.signatures.push(newSignature);
            }

            setFieldValue('attached_documents', updatedDocuments);

            if (previewDocument) {
                setPreviewDocument(prev => ({
                    ...prev,
                    signatures: currentDocument.signatures
                }));
            }

            setIsUploadModalOpen(false);
            setPreviewOpen(false);
            Swal.fire({
                title: 'Success!',
                text: 'Document has been signed successfully.',
                icon: 'success',
                timer: 2000,
                timerProgressBar: true
            });
        }

        setIsSignatureCreatorOpen(false);
        setShowInlineSignature(false);
        setCurrentDocumentIndex(null);
    };

    const handleReviewToggle = (documentId) => {
        setReviewedDocuments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(documentId)) {
                newSet.delete(documentId);
            } else {
                newSet.add(documentId);
            }
            return newSet;
        });
        setIsUploadModalOpen(false);
        setPreviewOpen(false);
        setIsSignatureCreatorOpen(false);
        setShowInlineSignature(false);
        setCurrentDocumentIndex(null);
    };

    const getDocumentStatus = (document) => {
        if (!document || isPreviewMode) return 'preview';
        if (!currentTenant) return 'no-tenant';

        const isUploadReq = isUploadRequired(document);
        const isSignatureReq = isSignatureRequiredForTenant(document, currentTenant?.id);
        const isSigned = hasTenantSigned(document, currentTenant?.id);
        const isReviewed = reviewedDocuments.has(document.id);

        if (isUploadReq) {
            return 'upload-required';
        } else if (isSignatureReq && !isSigned) {
            return 'signature-required';
        } else if (!isSignatureReq && !isReviewed) {
            return 'review-required';
        } else {
            return 'completed';
        }
    };

    const getActionMessage = (document, index) => {
        const status = getDocumentStatus(document);

        switch (status) {
            case 'upload-required':
                return {
                    message: "Click to Upload",
                    action: "upload",
                    color: "warning.main"
                };
            case 'signature-required':
                return {
                    message: "Click to Sign",
                    action: "preview_and_sign",
                    color: "error.main"
                };
            case 'review-required':
                return {
                    message: "Click to Review",
                    action: "review",
                    color: "info.main"
                };
            case 'completed':
                return null;
            default:
                return null;
        }
    };

    return (
        <Box className="lease-form-step7">
            <Snackbar
                open={showProgress}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ bottom: { xs: 90, sm: 30 } }}
            >
                <Alert severity="info" variant="filled" sx={{ width: '100%', minWidth: 300 }}>
                    <Typography variant="body2" mb={1}>
                        {progress < 100 ? 'Downloading document(s)...' : 'Download completed!'}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: 'white'
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" color="white">{`${Math.round(progress)}%`}</Typography>
                        {progress < 100 && (
                            <Button
                                size="small"
                                variant="text"
                                color="inherit"
                                onClick={() => setShowProgress(false)}
                                sx={{ minWidth: 'auto', p: 0, textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                        )}
                    </Box>
                </Alert>
            </Snackbar>

            <Box className="documents">
                <Typography variant="h6" align="left" gutterBottom className="notice">
                    DOCUMENTS AND SIGNATURES {isPreviewMode ? '(PREVIEW ONLY)' : ''}
                </Typography>

                <Typography variant="body2" align="left" className="notice-desc" sx={{ mb: 2 }}>
                    {isPreviewMode
                        ? "The following documents are part of your lease agreement. This is a preview-only interface."
                        : "The following documents are part of your lease agreement. Please review and sign the required documents."
                    }
                </Typography>

                {!isPreviewMode && !areAllDocumentsCompleted() && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Please complete all required actions (upload, sign, or review) for all documents before proceeding.
                        </Typography>
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Box className="documents-details-table" sx={{ my: 2 }}>
                    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Document Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {safeArrayLength(attached_documents) > 0 ? (
                                    attached_documents.map((document, index) => {
                                        if (!document) {
                                            return (
                                                <TableRow key={index}>
                                                    <TableCell colSpan={3} align="center" sx={{ color: '#f44336' }}>
                                                        Invalid document at index {index}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }

                                        const validUrlsCount = getValidUrlsCount(document);
                                        const actionMessage = getActionMessage(document, index);
                                        const status = getDocumentStatus(document);
                                        const isReviewed = reviewedDocuments.has(document.id);
                                        const isUploadReq = isUploadRequired(document);
                                        const isSignatureReq = isSignatureRequiredForTenant(document, currentTenant?.id);
                                        const isSigned = hasTenantSigned(document, currentTenant?.id);

                                        return (
                                            <TableRow key={document.id || index}>
                                                <TableCell sx={{ width: '34%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2">
                                                            {document.name || `Document ${index + 1}`}
                                                        </Typography>
                                                        {validUrlsCount > 1 && (
                                                            <Badge badgeContent={validUrlsCount} color="primary">
                                                                <AttachFileIcon fontSize="small" />
                                                            </Badge>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ width: '33%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {status === 'completed' && (
                                                            <Chip
                                                                icon={<CheckCircleIcon />}
                                                                label={
                                                                    isSigned ? "Signed" :
                                                                        isReviewed ? "Reviewed" :
                                                                            "Completed"
                                                                }
                                                                color="success"
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        )}
                                                        {status === 'upload-required' && (
                                                            <Chip
                                                                icon={<PendingIcon />}
                                                                label="Upload Required"
                                                                color="warning"
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        )}
                                                        {status === 'signature-required' && (
                                                            <Chip
                                                                icon={<ErrorIcon />}
                                                                label="Signature Required"
                                                                color="error"
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        )}
                                                        {status === 'review-required' && (
                                                            <>
                                                                <Chip
                                                                    icon={<InfoIcon />}
                                                                    label="Review Required"
                                                                    color="info"
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                                <Tooltip title={isReviewed ? "Click to unmark as reviewed" : "Click to mark as reviewed"} arrow>
                                                                    <Chip
                                                                        icon={isReviewed ? <CheckCircleIcon /> : null}
                                                                        label={isReviewed ? "Reviewed" : "Mark as Reviewed"}
                                                                        color={isReviewed ? "success" : "warning"}
                                                                        variant={isReviewed ? "filled" : "outlined"}
                                                                        size="small"
                                                                        onClick={() => handleReviewToggle(document.id)}
                                                                        sx={{ ml: 1, cursor: 'pointer' }}
                                                                    />
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ width: '33%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {actionMessage && (
                                                            <Typography
                                                                variant="body2"
                                                                color={actionMessage.color}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    textDecoration: 'underline',
                                                                    fontWeight: 500
                                                                }}
                                                                onClick={() => handleAction(actionMessage.action, document, index)}
                                                            >
                                                                {actionMessage.message}
                                                            </Typography>
                                                        )}
                                                        {status === 'completed' && (
                                                            <Typography
                                                                variant="body2"
                                                                color="success.main"
                                                                sx={{ fontWeight: 500 }}
                                                            >
                                                                Completed
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ color: '#aaa' }}>
                                            No Documents added yet.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Paper>
                </Box>
            </Box>

            <Dialog
                open={previewOpen}
                onClose={handleClosePreview}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { height: '90vh' }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {previewDocument?.name || 'Document Preview'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {previewDocument && !isUploadRequired(previewDocument) && (
                            <IconButton
                                onClick={() => handleDownloadDocument(previewDocument)}
                                size="small"
                                title="Download Document"
                            >
                                <ReactFeather icon="download" size={20} />
                            </IconButton>
                        )}
                        <IconButton onClick={handleClosePreview} size="small">
                            <ReactFeather icon='x-circle' size={20} />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'flex', height: '100%' }}>
                    {previewDocument && (
                        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
                            <Box sx={{
                                flex: showInlineSignature ? '0 0 60%' : '1 1 100%',
                                height: '100%',
                                position: 'relative',
                                overflowY: 'auto',
                            }}>
                                {isLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <CircularProgress />
                                    </Box>
                                ) : error ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography color="error">{error}</Typography>
                                    </Box>
                                ) : previewDocument.signedUrlsResults.length === 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography>No documents available for preview.</Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ p: 2 }}>
                                        {previewDocument.signedUrlsResults.map((result, idx) => (
                                            <Box key={idx} sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Document {idx + 1}
                                                </Typography>
                                                {result.error ? (
                                                    <Typography color="error">{result.error}</Typography>
                                                ) : previewDocument.loadingUrls[idx] ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                    <Previewer
                                                        url={result.file_url}
                                                        signedUrl={result.signedUrl}
                                                        width="100%"
                                                        height="75vh"
                                                    />
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                            {showInlineSignature && (
                                <>
                                    <Divider orientation="vertical" flexItem />
                                    <Box sx={{
                                        flex: '0 0 40%',
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: '#f8f9fa'
                                    }}>
                                        <Typography variant="h6" gutterBottom>
                                            Sign Document
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                            Please create your signature below to sign this document.
                                        </Typography>
                                        <Box sx={{ flex: 1 }}>
                                            <SignatureCreator
                                                isOpen={true}
                                                onClose={() => setShowInlineSignature(false)}
                                                onSign={handleSignatureCreated}
                                                onSignAndSave={handleSignatureCreated}
                                                initialSignature=""
                                                width={600}
                                                height={200}
                                                embedded={true}
                                                disableSignAndSave={true} // Added to disable Sign & Save button
                                            />
                                        </Box>
                                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={() => setShowInlineSignature(false)}
                                                fullWidth
                                            >
                                                Cancel
                                            </Button>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {previewDocument &&
                        !isPreviewMode &&
                        getDocumentStatus(previewDocument) === 'review-required' && (
                            <Button
                                variant={reviewedDocuments?.has(previewDocument.id) ? "contained" : "outlined"}
                                color={reviewedDocuments?.has(previewDocument.id) ? "success" : "primary"}
                                size="small"
                                startIcon={reviewedDocuments?.has(previewDocument.id)
                                    ? <CheckCircleIcon />
                                    : <InfoIcon />
                                }
                                onClick={() => handleReviewToggle(previewDocument.id)}
                                sx={{
                                    mr: 'auto',
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 0.8,
                                    minWidth: 'auto',
                                    transition: 'all 0.2s ease-in-out',
                                    ...(reviewedDocuments?.has(previewDocument.id) && {
                                        backgroundColor: 'success.main',
                                        '&:hover': {
                                            backgroundColor: 'success.dark',
                                        },
                                    }),
                                }}
                            >
                                {reviewedDocuments?.has(previewDocument.id)
                                    ? 'Reviewed'
                                    : 'Mark as Reviewed'
                                }
                            </Button>
                        )}
                    {previewDocument &&
                        !isPreviewMode &&
                        isSignatureRequiredForTenant(previewDocument, currentTenant?.id) &&
                        !hasTenantSigned(previewDocument, currentTenant?.id) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<ReactFeather icon="edit-3" size={16} />}
                                onClick={() => setShowInlineSignature(true)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1,
                                    boxShadow: 2,
                                }}
                            >
                                Sign Document
                            </Button>
                        )}
                    <Button onClick={handleClosePreview}>Close</Button>
                </DialogActions>
            </Dialog>

            {currentDocumentIndex !== null && currentTenant && !isPreviewMode && (
                <SignatureCreator
                    isOpen={isSignatureCreatorOpen}
                    onClose={() => setIsSignatureCreatorOpen(false)}
                    onSign={handleSignatureCreated}
                    onSignAndSave={handleSignatureCreated}
                    initialSignature=""
                    width={600}
                    height={200}
                    disableSignAndSave={true}
                />
            )}

            {documentToUpload && !isPreviewMode && (
                <MultiDocumentUploader
                    isOpen={isUploadModalOpen}
                    setOpen={setIsUploadModalOpen}
                    onClose={() => {
                        setIsUploadModalOpen(false);
                        setTimeout(() => setDocumentToUpload(null), 300);
                    }}
                    document={documentToUpload}
                    onDocumentUploaded={handleDocumentUploaded}
                    token={token}
                    maxFiles={maxFilesAllowed}
                />
            )}
        </Box>
    );
};

export { Step7 };